import axios from "axios";
import {
  RequestPriority,
  IdeaPriority,
} from "./types/types";
import { MemberItem } from "./components/CreateNewModal/FeedbackMenu/MemberItem";
import { UserItem } from "./components/CreateNewModal/FeedbackMenu/UserItem";
import { AccountItem } from "./components/CreateNewModal/FeedbackMenu/AccountItem";
import { EvidenceItem } from "./components/CreateNewModal/FeedbackMenu/EvidenceItem";
import {IntercomProps} from "react-use-intercom";
import {useCallback, useEffect, useRef} from "react";
import {throttle} from "lodash";
import {CurrentUser} from "./api/auth";


const padTo2Digits = (num: number) => {
  return num.toString().padStart(2, "0");
};

export const getDateString = (date: Date) => {
  return [
    padTo2Digits(date.getDate()),
    padTo2Digits(date.getMonth() + 1),
    date.getFullYear(),
  ].join("/");
};

export const truncateText = (text: string, maxLength: number) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

export const capitalizeFirstLetters = (str: string) => {
  //from https://flexiple.com/javascript-capitalize-first-letter/
  const arr = str.split(" ");
  for (let i = 0; i < arr.length; i++) {
    arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
  }
  return arr.join(" ");
};

export const completeOAuth = (
  authToken: string,
  integrationType: string,
  code: string,
  subdomain?: string
) => {
  return new Promise<any>((resolve, reject) => {

    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    };
    // if ((integrationType === 'salesforce' || integrationType === 'zendesk') && !subdomain) {
    //   reject("no subdomain provided");
    //   return
    // }

    if (integrationType === "jira") {
      axios
        .post(
          window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/authorize/jira",
          { code },
          config
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    } else if (integrationType === "notion") {
      axios
        .post(
          window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/authorize/notion",
          { code },
          config
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    } else if (integrationType === "slack") {
      axios
        .post(
          window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/authorize/slack",
          { code },
          config
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    } else if (integrationType === "gong") {
      axios
        .post(
          window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/authorize/gong",
          { code },
          config
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    } else if (integrationType === "zendesk") {
      if (!subdomain) {
        reject("no subdomain provided");
      } else {
        axios
          .post(
            window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN +
              "/authorize/zendesk",
            { code, subdomain },
            config
          )
          .then((response) => {
            resolve(response);
          })
          .catch((error) => reject(error));
      }
    } else if (integrationType === "salesforce") {
      if (!subdomain) {
        reject("no subdomain provided");
      } else {
        axios
          .post(
            window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN +
              "/authorize/salesforce",
            { code, subdomain },
            config
          )
          .then((response) => {
            resolve(response);
          })
          .catch((error) => reject(error));
      }
    } else if (integrationType === "clickup") {
      axios
        .post(
          window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/authorize/clickup",
          { code },
          config
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    }  else if (integrationType === "jira") {
      axios
        .post(
          window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/authorize/jira",
          { code },
          config
        )
        .then((response) => {
          resolve(response);
        })
        .catch((error) => reject(error));
    } else if (integrationType === "productboard") {
      axios.post(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/authorize/oauth2`,
        { integrationType, code, subdomain },
        config
      ).then((response) => {
        resolve(response);
      }).catch((error) => reject(error));
    } else if (integrationType === "intercom") {
      axios.post(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/authorize/oauth2`,
        { integrationType, code },
        config
      ).then((response) => {
        resolve(response);
      }).catch((error) => reject(error));
    }else {
      console.log("intergation type not supported " + integrationType);
    }
  });
};

export const stripHtml = (html: string) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;

  let text = tmp.textContent || tmp.innerText || "";

  // Replace any literal "\n" (backslash + n) with an actual newline
  text = text.replace(/\\n/g, "\n");

  // handle "\r\n":
  text = text.replace(/\\r\\n/g, "\n");

  return text;
};

// Caps a fallback description to a fixed word count so a cell that falls back from a
// blank title to body text shows a short, intentional snippet — not a full paragraph
// relying on CSS line-clamp alone to hide the rest.
export const truncateToWords = (text: string, wordLimit: number) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) return words.join(" ");
  return `${words.slice(0, wordLimit).join(" ")}...`;
};

// Resolves a display title for an evidence item: returns the explicit title when present,
// falls back to a 12-word snippet from the body text (HTML-stripped), then to `fallback`.
export const getEvidenceDisplayTitle = (
  title: string | undefined | null,
  text: string | undefined | null,
  fallback = '',
): string => {
  if (title) return title;
  if (text) return truncateToWords(stripHtml(text), 12);
  return fallback;
};

export const getDisplayRequestPriority = (priority: string) => {
  return IdeaPriority[priority] ? IdeaPriority[priority] : "Unknown";
};

export const getDisplayFeedbackPriority = (status: string) => {
  return RequestPriority[status] ? RequestPriority[status] : "Unknown";
};

/** The signed-in user, as returned by `GET /auth/me`. */
type MaybeUser = CurrentUser | null | undefined;

export const getConnectedUserRole = (user: MaybeUser) => {
  return user?.role || "viewer";
};

export const getConnectedUserEmail = (auth0: any) => {
  return auth0.user ? auth0.user.email : undefined;
};

const isGetBagelEmail = (email: string | null | undefined) =>
  typeof email === "string" && email.toLowerCase().endsWith("@getbagel.com");

export const isBagelSuperUser = (user: MaybeUser) => {
  return isGetBagelEmail(user?.email) && user?.isSuperUser === true;
};

export const isBagelAdmin = (user: MaybeUser) => {
  return isGetBagelEmail(user?.email) && user?.role === "admin";
};

/**
 * Check if the user is a Bagel employee (@getbagel.com email).
 * Use for features restricted to internal users only.
 */
export const isBagelEmployee = (user: MaybeUser): boolean => {
  return isGetBagelEmail(user?.email);
};

/**
 * List of Bagel lead emails with elevated access
 */
const BAGEL_LEAD_EMAILS = ['itai@getbagel.com', 'meir@getbagel.com'];

/**
 * Check if the user is a Bagel lead (itai or meir)
 * Used for sensitive admin operations restricted to core team
 */
export const isBagelLead = (user: MaybeUser): boolean => {
  const email = user?.email;
  return typeof email === 'string' && BAGEL_LEAD_EMAILS.includes(email.toLowerCase());
};

export const isBagelAdminOrManager = (user: MaybeUser) => {
  return (
    isGetBagelEmail(user?.email) &&
    (user?.role === "admin" || user?.role === "manager")
  );
};

export const isAccountAdmin = (user: MaybeUser) => {
  return user?.role === "admin";
};

export const getIntercomInitPropsForUser = (user: MaybeUser) => {
  const intercomInitProps: IntercomProps = {};
  if (user) {
    intercomInitProps.email = user.email;
    intercomInitProps.name = user.name ?? undefined;
    intercomInitProps.customAttributes = { bagelId: user.id };
  }
  return intercomInitProps;
};

const requestIcons: any = {
  conversation: "/evidence/Case.png",
  opportunity: "/evidence/Opportunity.png",
  call: "/evidence/Call.png",
  company: "/evidence/Account.png",
  account: "/evidence/Account.png",
  user: "/evidence/User.png",
  member: "/evidence/Member.png",
};

export const getRequestIconByType = (type: string) => {
  return requestIcons[type] ? requestIcons[type] : undefined;
};


export const itemPropsByType: any = {
  member: MemberItem,
  user: UserItem,
  account: AccountItem,
  opportunity: EvidenceItem,
  call: EvidenceItem,
  conversation: EvidenceItem,
};

export function getQuarter(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth();
  const quarter = Math.ceil((month + 1) / 3);
  let quarterWord = "";

  switch (quarter) {
  case 1:
    quarterWord = `Q1 ${year}`;
    break;
  case 2:
    quarterWord = `Q2 ${year}`;
    break;
  case 3:
    quarterWord = `Q3 ${year}`;
    break;
  case 4:
    quarterWord = `Q4 ${year}`;
    break;
  default:
    quarterWord = "Invalid quarter";
  }
  return quarterWord;
}


export const logout = async (auth0: any) => {
  localStorage.clear()
  return auth0.logout({logoutParams: { returnTo: window.location.origin  }})
};

export function useThrottle(cb: any, delay: any) {
  const options = { leading: false, trailing: true }; // add custom lodash options
  const cbRef = useRef(cb);
  // use mutable ref to make useCallback/throttle not depend on `cb` dep
  useEffect(() => { cbRef.current = cb; });
  return useCallback(
    throttle((...args) => cbRef.current(...args), delay, options),
    [delay]
  );
}

export function updateAccessorByState(
  accessor: string,
  wantedState: string
): string {
  const aggregationKeyword = "aggregations";
  const states = ["assigned", "suggested", "total"];

  // If the accessor does not contain the aggregation keyword, return it unchanged.
  if (!accessor?.includes(aggregationKeyword)) return accessor;

  // Split the accessor into its path segments.
  const segments = accessor.split(".");

  // Check if any of the segments is one of the states and replace it with the wanted state.
  for (let i = 0; i < segments.length; i++) {
    if (states.includes(segments[i])) {
      segments[i] = wantedState;
      break;
    }
  }

  // Reconstruct the accessor string from the segments.
  return segments.join(".");
}

/**
 * Extracts aggregation column names from column accessors
 * Example: "aggregations.companyIds.total" -> "companyIds"
 * @param columns - Array of column objects with accessor property
 * @returns Array of unique aggregation column names
 */
export function extractAggregationColumns(columns: any[]): string[] {
  const aggregationColumns: string[] = [];
  
  columns?.forEach((column) => {
    const accessor = column?.accessor;
    if (accessor && typeof accessor === 'string' && accessor.includes('aggregations.')) {
      // Split the accessor: "aggregations.companyIds.total" -> ["aggregations", "companyIds", "total"]
      const segments = accessor.split('.');
      
      // We expect the pattern: aggregations.{columnName}.{state}
      // So the column name should be at index 1
      if (segments.length >= 3 && segments[0] === 'aggregations') {
        const columnName = segments[1];
        
        // Only add if not already in the array
        if (columnName && !aggregationColumns.includes(columnName)) {
          aggregationColumns.push(columnName);
        }
      }
    }
  });
  
  return aggregationColumns;
}

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);

  // Formatting options for different parts
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date); // Monday
  const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date); // January
  const day = new Intl.DateTimeFormat('en-US', { day: '2-digit' }).format(date); // 08
  const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(date); // 2024

  // Format time parts with 12-hour clock
  const hour = new Intl.DateTimeFormat('en-US', { hour: '2-digit', hour12: true }).format(date); // 06
  const minute = new Intl.DateTimeFormat('en-US', { minute: '2-digit' }).format(date); // 08
  const second = new Intl.DateTimeFormat('en-US', { second: '2-digit' }).format(date); // 18

  // Extract AM/PM from the formatted hour
  const ampm = hour.includes('AM') ? 'AM' : 'PM';

  // Correct the hour formatting for 12-hour clock (without AM/PM part)
  const formattedHour = hour.replace(/(AM|PM)/, '').trim(); // Remove AM/PM from the hour

  // Combine into the desired format
  return `${weekday}, ${month} ${day}, ${year} at ${formattedHour}:${minute}:${second} ${ampm}`;
};

